import axios from "axios";
import { resolveShioriAccessRecovery } from "@/api/shiori-access-context.js";
import {
  decorateSessionRequest,
  expireRequestSessionIfCurrent,
  isRequestSessionCurrent,
  withoutSessionAuth,
} from "@/api/session-context.js";
import { invalidateShioriDestinationImageWorkflows } from "@/utils/ensure-shiori-destination-image.js";

const API_URL = process.env.VUE_APP_API_URL || "/api"; // FastAPIのURL

// デフォルトタイムアウト: 30秒
axios.defaults.timeout = 30000;

// ログイン
export async function login(email, password) {
  const res = await axios.post(
    `${API_URL}/login`,
    { email, password },
    withoutSessionAuth({}, "login"),
  );
  return res.data;
}

// 新規登録
export async function register(email, password, user_name) {
  const res = await axios.post(
    `${API_URL}/register`,
    { email, password, user_name },
    withoutSessionAuth({}, "register"),
  );
  return res.data;
}

// パスワードリセットリクエスト
export async function requestPasswordReset(email) {
  const res = await axios.post(
    `${API_URL}/password-reset-request`,
    { email },
    withoutSessionAuth({}, "password-reset"),
  );
  return res.data;
}

// 削除済みアカウントの復元メールをリクエスト
export async function requestAccountRecovery(email) {
  const res = await axios.post(
    `${API_URL}/auth/account-recovery/request`,
    { email },
    withoutSessionAuth({}, "account-recovery-request"),
  );
  return res.data;
}

// ワンタイムトークンで削除済みアカウントを復元
export async function confirmAccountRecovery(token) {
  const res = await axios.post(
    `${API_URL}/auth/account-recovery/confirm`,
    { token },
    withoutSessionAuth({}, "account-recovery-confirm"),
  );
  return res.data;
}

// 現在のユーザーのプロフィール画像を永続削除
export async function deleteProfileImage() {
  const res = await axios.delete(`${API_URL}/users/me/profile-image`);
  return res.data;
}

// Google OAuth
export async function loginWithGoogle() {
  // Google OAuth フローを開始
  window.location.href = `${API_URL}/auth/google`;
}

// ユーザー情報取得
export async function fetchMe(token) {
  const res = await axios.get(`${API_URL}/users/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

// Axiosインターセプターを設定してトークンを自動的に付与
axios.interceptors.request.use(
  (config) => decorateSessionRequest(config),
  (error) => {
    return Promise.reject(error);
  },
);

// レスポンスインターセプターで401/403エラーを処理
axios.interceptors.response.use(
  (response) => {
    if (!isRequestSessionCurrent(response.config)) {
      return Promise.reject(
        new axios.CanceledError("Response belongs to an inactive session"),
      );
    }
    return response;
  },
  (error) => {
    if (!isRequestSessionCurrent(error.config)) {
      return Promise.reject(error);
    }
    const status = error.response?.status;
    const recovery = resolveShioriAccessRecovery(error.config, status);
    if (recovery) {
      window.dispatchEvent(
        new CustomEvent("rabiitrip:shiori-access-denied", {
          detail: recovery,
        }),
      );
    }

    if (status === 401 && !error.config?.suppressSessionRedirect) {
      // Only the response belonging to the current session may expire it.
      if (expireRequestSessionIfCurrent(error.config)) {
        invalidateShioriDestinationImageWorkflows();
        window.dispatchEvent(new CustomEvent("rabiitrip:session-expired"));
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    } else if (status === 403) {
      import("@/store/notification-store").then(({ useNotificationStore }) => {
        const notificationStore = useNotificationStore();
        console.error("API request denied (403)");
        // バックエンドから返された実際のエラーメッセージを使用する
        // ハードコードされたメッセージではなく、各APIが返す具体的なメッセージ（例: 「このボードへのアクセス権限がありません」「このしおりにアクセスする権限がありません」）を表示する
        const errorMessage =
          error.response?.data?.detail ||
          "アクセス権限がありません";
        notificationStore.showError(errorMessage);
      });
    } else if (status >= 500) {
      import("@/store/notification-store").then(({ useNotificationStore }) => {
        const notificationStore = useNotificationStore();
        console.error(`API request failed (${status})`);
        notificationStore.showError(
          "サーバーでエラーが発生しました。時間をおいて再度お試しください。",
        );
      });
    }
    return Promise.reject(error);
  },
);
